import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Column, DataTable } from "@/components/ui/data-table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSorting } from "@/hooks/useSorting";
import { formatCurrencyAmount } from "@/components/pages/services/utils/priceFormatting";
import type { PartnerHotelCurationItemSource } from "@/types/partner-destination";
import { ServiceFeaturedSwitch } from "@/components/pages/guesteria-partners/components/ServiceFeaturedSwitch";
import type { ServiceFeaturedController } from "@/components/pages/guesteria-partners/hooks/useServiceFeatured";
import { GuesteriaCurationFiltersPopover } from "@/components/pages/guesteria-partners/components/GuesteriaCurationFiltersPopover";
import {
  countActiveCurationFilters,
  DEFAULT_CURATION_FILTERS,
  type CurationFiltersState,
} from "@/components/pages/guesteria-partners/utils/guesteriaCurationFilters";
import {
  filterActivityTableRows,
  sortActivityRows,
  type ActivityTableRow,
} from "@/components/pages/guesteria-partners/utils/guesteriaActivitiesTable";
import {
  GUESTERIA_SERVICES_PAGE_SIZE,
  getTotalPages,
  paginateRows,
  syncPaginationState,
  type PaginationState,
} from "@/components/pages/guesteria-partners/utils/guesteriaServicesPagination";

export type GuesteriaActivityRow = ActivityTableRow & {
  source: PartnerHotelCurationItemSource;
  price: number | null;
  currency: string | null;
  imageUrl: string | null;
  /** Hotel service of the activity; `null`: none yet (hidden or not saved). */
  serviceId: string | null;
  /** `true`: added by the hotel (can be removed); `false`: city list item. */
  addedByHotel: boolean;
};

export type GuesteriaActivitiesTableProps = {
  rows: GuesteriaActivityRow[];
  canEdit: boolean;
  /** Rights to change the Featured Activities list of the guest app. */
  canToggleFeatured: boolean;
  featured: ServiceFeaturedController;
  onToggleHidden: (productCode: string, hidden: boolean) => void;
  onRemove: (productCode: string) => void;
};

const ActivityThumbnail = ({
  imageUrl,
}: {
  imageUrl: string | null;
}): React.JSX.Element => {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage =
    imageUrl !== null && imageUrl !== "" && failedUrl !== imageUrl;
  return (
    <div className="bg-muted size-10 shrink-0 overflow-hidden rounded-md">
      {showImage ? (
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          className="size-full rounded-md object-cover"
          onError={() => setFailedUrl(imageUrl)}
        />
      ) : null}
    </div>
  );
};

export const GuesteriaActivitiesTable = ({
  rows,
  canEdit,
  canToggleFeatured,
  featured,
  onToggleHidden,
  onRemove,
}: GuesteriaActivitiesTableProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { sortField, sortDirection, handleSort } = useSorting();
  const [search, setSearch] = useState("");

  const [curationFilters, setCurationFilters] = useState<CurationFiltersState>(
    DEFAULT_CURATION_FILTERS,
  );

  const filtersActive =
    search.trim() !== "" || countActiveCurationFilters(curationFilters) > 0;

  const visibleRows = useMemo(
    () =>
      sortActivityRows(
        filterActivityTableRows(rows, search, curationFilters),
        sortField,
        sortDirection,
      ),
    [rows, search, curationFilters, sortField, sortDirection],
  );

  const conditionsKey = useMemo(
    () => JSON.stringify([search, curationFilters, sortField, sortDirection]),
    [search, curationFilters, sortField, sortDirection],
  );
  const totalPages = getTotalPages(
    visibleRows.length,
    GUESTERIA_SERVICES_PAGE_SIZE,
  );
  const [storedPagination, setStoredPagination] = useState<PaginationState>({
    page: 1,
    conditionsKey,
  });
  const pagination = syncPaginationState(
    storedPagination,
    conditionsKey,
    totalPages,
  );
  if (pagination !== storedPagination) {
    setStoredPagination(pagination);
  }
  const pageRows = useMemo(
    () =>
      paginateRows(visibleRows, pagination.page, GUESTERIA_SERVICES_PAGE_SIZE),
    [visibleRows, pagination.page],
  );

  const featuredUnavailableReason = useCallback(
    (row: GuesteriaActivityRow): string | null => {
      if (!canToggleFeatured) {
        return t("partners.curation.featured.noPermission");
      }
      return row.hidden ? t("partners.curation.featured.hidden") : null;
    },
    [t, canToggleFeatured],
  );

  const columns: Column<GuesteriaActivityRow>[] = useMemo(
    () => [
      {
        key: "name",
        header: t("partners.curation.table.name"),
        sortable: true,
        className: "flex-1",
        render: (_v, row) => (
          <div className="flex min-w-0 items-center gap-3">
            <ActivityThumbnail imageUrl={row.imageUrl} />
            <span className="truncate font-medium text-[#171717]">
              {row.title}
            </span>
          </div>
        ),
      },
      {
        key: "top",
        header: t("partners.curation.table.top"),
        sortable: true,
        className: "w-[80px] text-center",
        render: (_v, row) => (
          <span className="text-sm text-[#171717]">{row.position}</span>
        ),
      },
      {
        key: "price",
        header: t("partners.curation.table.price"),
        sortable: false,
        className: "w-[160px]",
        render: (_v, row) =>
          row.price === null ? (
            <span className="text-sm text-[#171717]">—</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-sm text-[#171717]">
              {formatCurrencyAmount(row.price, row.currency)}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={0}
                    className="text-muted-foreground cursor-help rounded border border-[#E5E5E5] px-1 text-xs"
                  >
                    {t("partners.curation.table.priceNet")}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {t("partners.curation.table.priceNetHint")}
                </TooltipContent>
              </Tooltip>
            </span>
          ),
      },
      {
        key: "featured",
        header: t("partners.curation.table.featured"),
        sortable: false,
        className: "w-[100px] text-center",
        render: (_v, row) => (
          <ServiceFeaturedSwitch
            serviceId={row.serviceId}
            featured={featured}
            unavailableReason={featuredUnavailableReason(row)}
          />
        ),
      },
      {
        key: "actions",
        header: t("partners.curation.table.actions"),
        sortable: false,
        className: "w-[120px] text-center",
        render: (_v, row) =>
          row.addedByHotel ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canEdit}
              onClick={() => onRemove(row.productCode)}
            >
              {t("partners.curation.remove")}
            </Button>
          ) : (
            <Switch
              checked={!row.hidden}
              disabled={!canEdit}
              onCheckedChange={(checked) =>
                onToggleHidden(row.productCode, !checked)
              }
              aria-label={t("partners.curation.visibleToGuests")}
            />
          ),
      },
    ],
    [t, canEdit, featured, featuredUnavailableReason, onToggleHidden, onRemove],
  );

  const resetFilters = () => {
    setSearch("");
    setCurationFilters(DEFAULT_CURATION_FILTERS);
  };

  const emptyMessage =
    rows.length === 0
      ? t("partners.curation.table.empty")
      : t("partners.curation.filters.noMatches");

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("partners.curation.table.searchPlaceholder")}
          aria-label={t("partners.curation.table.searchPlaceholder")}
          className="h-8 w-64"
        />
        <GuesteriaCurationFiltersPopover
          applied={curationFilters}
          onApply={setCurationFilters}
        />
        {filtersActive && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={resetFilters}
          >
            {t("partners.curation.filters.reset")}
          </Button>
        )}
      </div>
      <DataTable<GuesteriaActivityRow>
        data={pageRows}
        columns={columns}
        getRowKey={(row) =>
          `${row.addedByHotel ? "a" : "c"}-${row.productCode}`
        }
        columnHeaderClassName="text-xs font-semibold bg-gray-50"
        sorting={{
          field: sortField,
          direction: sortDirection,
          onSort: handleSort,
        }}
        emptyMessage={emptyMessage}
        pagination={
          totalPages > 1
            ? {
                currentPage: pagination.page,
                totalPages,
                onPageChange: (page) =>
                  setStoredPagination({ page, conditionsKey }),
              }
            : undefined
        }
      />
    </div>
  );
};
